import { ResponsiveViewService, responsiveViewToken } from 'go-modules/responsive-view/responsive-view.service';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { Subject, takeUntil, BehaviorSubject, timer, distinctUntilChanged, combineLatest, take, filter } from 'rxjs';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { Input } from '@angular/core';
import { OnInit } from '@angular/core';
import { FeedbackSessionService, FeedbackTabs } from '@go-modules/services/feedback-session/feedback-session.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { MediaDisplayMode } from 'go-modules/feedback-session/feedback-session.provider';
import { MatTabGroup } from '@angular/material/tabs';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';

export interface Bindings {
	hasMedia: boolean;
	displayMode: MediaDisplayMode;
}

@Component({
	selector: 'feedback-panel-tabs',
	templateUrl: './feedback-panel-tabs.component.html',
	styleUrls: ['./feedback-panel-tabs.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
	host: {
		'[class]': 'responsiveLayout.cssClassString()'
	}
})
export class FeedbackPanelTabsComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() public hasMedia: boolean;
	@Input() public displayMode: MediaDisplayMode;
	public activity: ActivityModel;
	public tabs = FeedbackTabs;
	public newComments: number = 0;
	public showNewCommentsIndicator$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public isPulsing$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public selectedIndex$: BehaviorSubject<number> = new BehaviorSubject(0);
	private destroyed$ = new Subject<void>();
	@ViewChild('tabGroup') private tabGroup: MatTabGroup;
	@ViewChild('expandButton') private expandButton?: ElementRef<HTMLButtonElement>;

	constructor (
		public feedbackSessionService: FeedbackSessionService,
		private eventService: EventService,
		@Inject(responsiveViewToken) private responsiveView: ResponsiveViewService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		public responsiveLayout: ResponsiveLayoutService
	) {}

	public ngOnInit () {
		this.activity = this.selectedService.getActivity();

		if (this.feedbackSessionService.getCurrentTab() === FeedbackTabs.TRANSCRIPTION &&
			!this.canSeeTranscriptions()) {
			this.feedbackSessionService.setActiveTab(FeedbackTabs.COMMENT);
		}

		this.eventService.listen([
			EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_SAVED,
			EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENTS_LOADED
		]).pipe(takeUntil(this.destroyed$))
			.subscribe((ev: GoEvent) => {
				if (this.feedbackSessionService.getCurrentTab() !== FeedbackTabs.COMMENT) {
					this.showNewCommentsIndicator$.next(true);
					if (ev.name === EVENT_NAMES.FEEDBACK_SESSION_AI_COMMENTS_LOADED) {
						this.newComments = ev.data;
					} else {
						this.newComments++;
					}
					this.isPulsing$.next(true);
					timer(1800).pipe(
						takeUntil(this.destroyed$)
					).subscribe(() => {
						this.isPulsing$.next(false);
					});
				}
			});
	}

	public ngAfterViewInit (): void {
		combineLatest([
			this.feedbackSessionService.isPanelOpen$,
			this.feedbackSessionService.activeTab$
		]).pipe(
			takeUntil(this.destroyed$),
			distinctUntilChanged()
		).subscribe(([isOpen, currentTab]) => {
			if (isOpen && this.tabGroup) {
				const visibleTabs = this.getVisibleTabs();
				this.selectedIndex$.next(visibleTabs.indexOf(currentTab));
			}
		});

		this.feedbackSessionService.panelToggleFocusRestoreRequest$.pipe(
			takeUntil(this.destroyed$),
			distinctUntilChanged(),
			filter((requestId) => requestId > 0)
		).subscribe(() => {
			this.restoreToggleFocus();
		});
	}

	public ngOnDestroy (): void {
		this.feedbackSessionService.resetRequestPanelToggleFocusRestore();
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	public getVisibleTabs (): FeedbackTabs[] {
		return [
			...(this.canSeeCommentKind() ? [FeedbackTabs.COMMENT] : []),
			...(this.canSeeRubricKind() ? [FeedbackTabs.RUBRIC] : []),
			FeedbackTabs.INFORMATION,
			...(this.canSeeTranscriptions() ? [FeedbackTabs.TRANSCRIPTION] : []),
			...(this.canSeeAnalytics() ? [FeedbackTabs.ANALYTICS] : [])
		];
	}

	public changeTab (index: number): void {
		if (this.responsiveView.showView('FEEDBACK_PANEL_MOBILE_VIEW') && !this.feedbackSessionService.getIsPanelOpen()) {
			this.eventService.broadcast(EVENT_NAMES.FEEDBACK_SESSION_FEEDBACK_EXPANDED);
		}

		const tab = this.getVisibleTabs().at(index);

		if (tab === FeedbackTabs.COMMENT) {
			this.newComments = 0;
			this.showNewCommentsIndicator$.next(false);
		}

		this.feedbackSessionService.setActiveTab(tab);
		this.feedbackSessionService.togglePanel(true);
	}

	public togglePanel (): void {
		this.feedbackSessionService.requestPanelToggleFocusRestore();
		this.feedbackSessionService.togglePanel();
	}

	private restoreToggleFocus (): void {
		timer(0).pipe(take(1)).subscribe(() => {
			this.expandButton.nativeElement.focus();
		});
	}

	public canSeeCommentKind = (): boolean => {
		return this.activity.isCommentsEnabled()
			&& (this.displayMode !== MediaDisplayMode.RECORD || this.activity.live_session_enabled);
	};

	public canSeeRubricKind = (): boolean => {
		if (this.displayMode === MediaDisplayMode.RECORD && !this.activity.live_session_enabled) {
			return false;
		}
		if (this.activity.isPeerRubricEnabled()) {
			return true;
		}
		if (this.activity.isRubricEnabled()) {
			const group = this.selectedService.getGroup();
			const session = this.selectedService.getSession();
			const user = this.userService.currentUser;
			return group.hasReviewerRole(true) || session.isOwner(user);
		}
		return false;
	};

	public canSeeAnalytics (): boolean {
		return this.displayMode === MediaDisplayMode.PLAYBACK;
	}

	public canSeeTranscriptions (): boolean {
		if (this.displayMode === MediaDisplayMode.RECORD || this.activity.isCommentOnly()) {
			return false;
		}

		const license = this.selectedService.getLicense();
		const hasLicenseAccess = !license ||
			license.salesforce_license.may_tease ||
			license.salesforce_license.transcriptions_enabled;

		return hasLicenseAccess;
	}

	public shouldShowToggleFeedbackPanel (): boolean {
		return this.hasMedia || this.activity.has_response_media;
	}
}
